import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import TimelineList from '../components/TimelineList';
import SessionDetailPane from '../components/SessionDetailPane';
import SessionComparison from '../components/SessionComparison';
import SessionFilters from '../components/SessionFilters';
import AddSessionDrawer from '../components/AddSessionDrawer';
import StudentSelector from '../components/StudentSelector';
import SessionsEmptyState from '../components/SessionsEmptyState';
import { useWorkflow } from '../context/WorkflowContext';
import { apiFetch } from '../api';
import useScopedStudents from '../hooks/useScopedStudents';
import { toast } from '../utils/toast';

const SESSIONS_LAST_STUDENT_KEY = 'lrbrain-sessions-last-student-uuid';

const readLastStudentFromSession = () => {
  try {
    return sessionStorage.getItem(SESSIONS_LAST_STUDENT_KEY);
  } catch {
    return null;
  }
};

const writeLastStudentToSession = (uuid) => {
  try {
    if (uuid) {
      sessionStorage.setItem(SESSIONS_LAST_STUDENT_KEY, uuid);
      return;
    }
    sessionStorage.removeItem(SESSIONS_LAST_STUDENT_KEY);
  } catch {}
};

const Sessions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSessionId, setSelectedSessionId] = useState(() => searchParams.get('id'));
  const [allSessions, setAllSessions] = useState([]);
  const [mode, setMode] = useState(() => (searchParams.get('compare') ? 'compare' : 'detail'));
  const [filters, setFilters] = useState({});
  const [showAddSession, setShowAddSession] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const { selectSession, studentContext } = useWorkflow();
  const { students, studentsLoading } = useScopedStudents();

  // Student selection state - check URL param first, then workflow context
  const [selectedStudentUuid, setSelectedStudentUuid] = useState(
    () => {
      const fromUrl = searchParams.get('student');
      if (fromUrl) return fromUrl;
      const fromSession = readLastStudentFromSession();
      if (fromSession) return fromSession;
      return studentContext?.uuid || null;
    }
  );

  const isInsecureNavigationError = useCallback((error) => {
    const message = (error?.message || '').toLowerCase();
    return error?.name === 'SecurityError' || message.includes('operation is insecure');
  }, []);

  const safeSetSearchParams = useCallback((nextParams, options = undefined) => {
    if (!nextParams) return false;
    const currentSerialized = searchParams.toString();
    const nextSerialized = nextParams.toString();
    if (currentSerialized === nextSerialized) return false;
    try {
      setSearchParams(nextParams, options);
      return true;
    } catch (err) {
      if (isInsecureNavigationError(err)) {
        console.warn('Search params update skipped due to browser security restrictions.', err);
        return false;
      }
      throw err;
    }
  }, [searchParams, setSearchParams, isInsecureNavigationError]);

  // Sync student selection from URL when it changes externally
  useEffect(() => {
    const studentParam = searchParams.get('student');
    if (studentParam && studentParam !== selectedStudentUuid) {
      const exists = students.some(s => s.uuid === studentParam);
      if (exists) {
        setSelectedStudentUuid(studentParam);
      } else if (!studentsLoading) {
        const next = new URLSearchParams(searchParams);
        next.delete('student');
        next.delete('id');
        safeSetSearchParams(next);
        setSelectedSessionId(null);
      }
    }
  }, [searchParams, students, studentsLoading, selectedStudentUuid, safeSetSearchParams]);

  // Update URL when student changes
  useEffect(() => {
    const currentStudentParam = searchParams.get('student');
    if (!selectedStudentUuid) {
      if (currentStudentParam) {
        const next = new URLSearchParams(searchParams);
        next.delete('student');
        next.delete('id');
        safeSetSearchParams(next);
      }
      return;
    }

    if (currentStudentParam === selectedStudentUuid) return;

    const next = new URLSearchParams(searchParams);
    next.set('student', selectedStudentUuid);
    next.delete('id');
    setSelectedSessionId(null);
    safeSetSearchParams(next);
  }, [selectedStudentUuid, searchParams, safeSetSearchParams]);

  useEffect(() => {
    if (!selectedStudentUuid) return;
    const exists = students.some(student => student.uuid === selectedStudentUuid);
    if (!exists && !studentsLoading) {
      setSelectedStudentUuid(null);
      setSelectedSessionId(null);
      setAllSessions([]);
      writeLastStudentToSession(null);
    }
  }, [students, studentsLoading, selectedStudentUuid]);

  useEffect(() => {
    if (!selectedStudentUuid) {
      writeLastStudentToSession(null);
      return;
    }
    if (!selectedStudentUuid || studentsLoading) return;
    const exists = students.some(student => student.uuid === selectedStudentUuid);
    if (!exists) return;
    writeLastStudentToSession(selectedStudentUuid);
  }, [selectedStudentUuid, students, studentsLoading]);

  const loadAllSessions = useCallback(async () => {
    if (!selectedStudentUuid) {
      setAllSessions([]);
      return;
    }

    setSessionsLoading(true);
    try {
      const params = new URLSearchParams({ filter: 'all', sort: 'desc' });
      params.set('student_id', selectedStudentUuid);
      if (filters.subject) params.set('subject', filters.subject);
      if (filters.date_range) params.set('date_range', filters.date_range);

      const response = await apiFetch(`/api/sessions/list?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setAllSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, [selectedStudentUuid, filters]);

  // Load sessions when student is selected or filters change
  useEffect(() => {
    if (selectedStudentUuid) {
      loadAllSessions();
    } else {
      setAllSessions([]);
    }
  }, [selectedStudentUuid, loadAllSessions]);

  // Handle student selection
  const handleStudentSelect = (uuid) => {
    setSelectedStudentUuid(uuid);
    setSelectedSessionId(null); // Clear session selection when student changes
    setAllSessions([]); // Clear sessions while loading new ones
  };

  // Get selected student info for display
  const selectedStudent = students.find(s => s.uuid === selectedStudentUuid);
  const studentDisplayName = selectedStudent ? [
    selectedStudent.first_name,
    selectedStudent.last_name
  ].filter(Boolean).join(' ') || selectedStudent.grade_year_level || 'Student' : null;

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Handler for when a new session is created via the drawer
  const handleSessionCreated = useCallback((newSession) => {
    // Reload sessions list to include the new session
    loadAllSessions();

    // Show success toast
    toast.success(`Session "${newSession.title || 'New Session'}" created and analyzed!`);

    // Close the drawer
    setShowAddSession(false);

    // Note: We could auto-select the new session here, but Google Meet transcripts
    // have different IDs than audio sessions, so we just refresh the list
  }, [loadAllSessions]);

  const handleSelectSession = (sessionId) => {
    setSelectedSessionId(sessionId);
    setMode('detail');
    const next = new URLSearchParams(searchParams);
    next.set('id', sessionId);
    safeSetSearchParams(next);
    try { localStorage.setItem('lrbrain-selected-session', sessionId); } catch {}

    // Update workflow context with selected session
    const session = allSessions.find(s => s.id === sessionId);
    if (session) {
      selectSession({
        id: session.id,
        title: session.preview?.title || session.id,
        date: session.preview?.date || session.date
      });
    }
  };

  // Removed unused handleCompareSession

  const clearSelection = () => {
    setSelectedSessionId(null);
    setMode('detail');
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    safeSetSearchParams(next);
  };

  // Restore selection from URL or localStorage on mount
  useEffect(() => {
    const fromUrl = searchParams.get('id');
    if (fromUrl) {
      setSelectedSessionId(fromUrl);
      return;
    }
    try {
      const saved = localStorage.getItem('lrbrain-selected-session');
      if (saved) setSelectedSessionId(saved);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="sessions-page">
      <div className="sessions-header sessions-header--expanded">
        <div className="sessions-header__content">
          <div className="sessions-header__student">
            <StudentSelector
              students={students}
              selectedStudentUuid={selectedStudentUuid}
              onSelect={handleStudentSelect}
              loading={studentsLoading}
            />
          </div>

          {selectedStudentUuid && (
            <>
              <SessionFilters
                onFiltersChange={handleFiltersChange}
                hideStudentFilter={true}
              />
              <button
                className="add-session-btn"
                onClick={() => setShowAddSession(true)}
                title="Add a new session manually"
              >
                <span className="btn-icon">+</span>
                <span className="btn-text">Add Session</span>
              </button>
            </>
          )}
        </div>
      </div>

      <AddSessionDrawer
        isOpen={showAddSession}
        onClose={() => setShowAddSession(false)}
        onSessionCreated={handleSessionCreated}
        students={students}
        loadingStudents={studentsLoading}
        selectedStudentUuid={selectedStudentUuid}
      />

      {/* Empty state: No student selected */}
      {!selectedStudentUuid && (
        <SessionsEmptyState type="no-student" />
      )}

      {/* Empty state: Student selected but no sessions */}
      {selectedStudentUuid && !sessionsLoading && allSessions.length === 0 && (
        <SessionsEmptyState type="no-sessions" studentName={studentDisplayName} />
      )}

      {/* Sessions view: Student selected and has sessions */}
      {selectedStudentUuid && (sessionsLoading || allSessions.length > 0) && (
        <div className="sessions-split">
          <aside className="timeline-pane">
            <TimelineList
              sortOrder="desc"
              selectedId={selectedSessionId}
              onSelect={handleSelectSession}
              filters={filters}
              sessions={allSessions}
              loading={sessionsLoading}
            />
          </aside>
          <section className={`detail-pane ${selectedSessionId ? 'detail-enter' : ''}`}>
            {!selectedSessionId && (
              <div className="detail-empty">
                <div className="empty-card">
                  <div className="icon">📚</div>
                  <div className="title">Select a session</div>
                  <div className="subtitle">Timeline on the left • Details appear here</div>
                </div>
              </div>
            )}
            {selectedSessionId && mode === 'detail' && (
              <SessionDetailPane
                sessionId={selectedSessionId}
                onClose={clearSelection}
                onCompare={() => setMode('compare')}
              />
            )}
            {selectedSessionId && mode === 'compare' && (
              <div className="detail-wrapper">
                <div className="detail-body">
                  <SessionComparison
                    baseSessionId={selectedSessionId}
                    compareSessionId={searchParams.get('compare') || ''}
                    onUpdateCompareId={(id) => {
                      const next = new URLSearchParams(searchParams);
                      if (id) next.set('compare', id); else next.delete('compare');
                      safeSetSearchParams(next);
                    }}
                    onSwap={() => {
                      const currentCompare = searchParams.get('compare');
                      if (!currentCompare) return;
                      const next = new URLSearchParams(searchParams);
                      next.set('id', currentCompare);
                      next.set('compare', selectedSessionId || '');
                      safeSetSearchParams(next);
                      setSelectedSessionId(currentCompare);
                    }}
                    onBack={() => setMode('detail')}
                    allSessions={allSessions}
                  />
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Sessions;
